// Client-side image compression — canvas-based, no external deps.
//
// `compressImage(file, opts)` returns a Promise<File>. If compression isn't
// applicable (non-image, GIF, SVG, error during compression, or the result
// would be larger than the original), the original file is returned
// unchanged so callers can use the result unconditionally.
//
// Defaults are tuned for architectural photography: 2000px max dimension at
// JPEG quality 0.82 typically keeps visual fidelity at <1MB per image — well
// under typical ingress limits and ~80% smaller than 10MB phone photos.

const DEFAULTS = {
  maxDimension: 2000,
  quality: 0.82,
  // Skip compression entirely for files smaller than this — overhead isn't worth it.
  minSizeBytes: 200 * 1024,
};

const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml']);

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

export const compressImage = async (file, opts = {}) => {
  const { maxDimension, quality, minSizeBytes } = { ...DEFAULTS, ...opts };

  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.has(file.type)) return file;
  if (file.size < minSizeBytes) return file;

  try {
    const img = await loadImage(file);
    const { width, height } = img;
    const longest = Math.max(width, height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, targetW, targetH);

    // Always export to JPEG — it gives the best size for photographs and
    // avoids surprises with animated PNGs etc.
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;

    // Preserve a sensible filename — strip the original extension and add .jpg
    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    // Any failure → fall back to the original file.
    return file;
  }
};

export const compressImages = async (files, opts) => {
  const results = await Promise.all(files.map((f) => compressImage(f, opts)));
  const originalBytes = files.reduce((s, f) => s + (f?.size || 0), 0);
  const compressedBytes = results.reduce((s, f) => s + (f?.size || 0), 0);
  return { files: results, originalBytes, compressedBytes };
};

export const formatBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
